import React, { useEffect, useState, useRef } from 'react'
import styled from 'styled-components'
import { FadeInUp, StaggerContainer, StaggerItem, AnimatedCounter, HoverCard } from '../Components/Animations'

// Design tokens
const colors = {
  primary: '#0d9488',
  primaryLight: '#14b8a6',
  primaryDark: '#0f766e',
  secondary: '#0891b2',
  accent: '#5eead4',
  dark: '#0f172a',
  darkBlue: '#1e3a5f',
  text: '#334155',
  textLight: '#64748b',
  white: '#ffffff',
  background: '#f8fafc',
  backgroundAlt: '#f0fdfa'
}

const Container = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: ${colors.white};
  font-family: -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
`

const MaxWidthContainer = styled.div`
  max-width: 1600px;
  margin: 0 auto;
  padding: 0 24px;
  @media (max-width: 768px) {
    padding: 0 16px;
  }
`

// Hero Section
const HeroSection = styled.section`
  padding: 120px 0 50px;
  background: linear-gradient(135deg, ${colors.dark} 0%, ${colors.darkBlue} 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 20% 80%, rgba(20, 184, 166, 0.15) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(8, 145, 178, 0.15) 0%, transparent 50%);
  }

  @media (max-width: 768px) {
    padding: 100px 0 40px;
  }
`

const HeroContent = styled.div`
  position: relative;
  z-index: 1;
  text-align: center;
  max-width: 900px;
  margin: 0 auto;
`

const HeroLabel = styled.span`
  display: inline-block;
  background: rgba(20, 184, 166, 0.2);
  color: ${colors.accent};
  padding: 8px 20px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 24px;
`

const HeroTitle = styled.h1`
  font-size: 56px;
  font-weight: 700;
  color: ${colors.white};
  margin-bottom: 24px;
  line-height: 1.1;

  @media (max-width: 768px) {
    font-size: 36px;
  }
`

const HeroSubtitle = styled.p`
  font-size: 20px;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.7;
  max-width: 700px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`

// Stats Section
const StatsSection = styled.section`
  padding: 0;
  margin-top: -60px;
  position: relative;
  z-index: 10;
`

const StatsCard = styled.div`
  background: ${colors.white};
  border-radius: 20px;
  padding: 32px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 32px;
  }
`

const StatItem = styled.div`
  text-align: center;
  padding: 20px;
  border-radius: 12px;
  transition: all 0.3s ease;

  &:hover {
    background: ${colors.backgroundAlt};
  }
`

const StatNumber = styled.div`
  font-size: 48px;
  font-weight: 700;
  color: ${colors.primary};
  margin-bottom: 8px;
  line-height: 1;

  @media (max-width: 768px) {
    font-size: 36px;
  }
`

const StatLabel = styled.div`
  font-size: 16px;
  color: ${colors.textLight};
  font-weight: 500;
`

// Culture Section
const CultureSection = styled.section`
  padding: 100px 0;
  background: ${colors.white};

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`

const SectionHeader = styled.div`
  text-align: center;
  max-width: 700px;
  margin: 0 auto 30px;
`

const SectionLabel = styled.span`
  display: inline-block;
  color: ${colors.primary};
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 2px;
  text-transform: uppercase;
  margin-bottom: 8px;
`

const SectionTitle = styled.h2`
  font-size: 36px;
  font-weight: 700;
  color: ${colors.dark};
  margin-bottom: 10px;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 28px;
  }
`

const SectionSubtitle = styled.p`
  font-size: 16px;
  color: ${colors.textLight};
  line-height: 1.6;
`

// Benefits Section
const BenefitsSection = styled.section`
  padding: 100px 0;
  background: ${colors.backgroundAlt};

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 16px;

  @media (max-width: 1600px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`

const BenefitCard = styled.div`
  background: ${colors.white};
  padding: 20px;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  }
`

const BenefitIcon = styled.div`
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, ${colors.primary}, ${colors.secondary});
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
  font-size: 24px;
`

const BenefitTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.dark};
  margin-bottom: 8px;
`

const BenefitDescription = styled.p`
  font-size: 12px;
  line-height: 1.5;
  color: ${colors.textLight};
`

// Values Section
const ValuesSection = styled.section`
  padding: 100px 0;
  background: ${colors.white};

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`

const ValuesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`

const ValueCard = styled.div`
  background: ${colors.background};
  padding: 20px;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateX(8px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.06);
  }
`

const ValueIcon = styled.div`
  width: 44px;
  height: 44px;
  min-width: 44px;
  background: linear-gradient(135deg, ${colors.primary}, ${colors.secondary});
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
`

const ValueContent = styled.div`
  flex: 1;
`

const ValueTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  color: ${colors.dark};
  margin-bottom: 4px;
`

const ValueText = styled.p`
  font-size: 13px;
  line-height: 1.5;
  color: ${colors.textLight};
`

// Jobs Section
const JobsSection = styled.section`
  padding: 100px 0;
  background: ${colors.background};

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`

const IframeContainer = styled.div`
  background: ${colors.white};
  border-radius: 20px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`

const RSNGCareers = () => {
  const [iframeKey, setIframeKey] = useState(0)
  const loadCountRef = useRef(0)

  const ZENOHIRE_URL = "https://zenohire.com/findjobs?accountId=690073ab56a394c480daee28&status=active&embed=true"

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const handleIframeLoad = () => {
    loadCountRef.current += 1
    // Load count tracking:
    // 1: Initial page load (filtered jobs list)
    // 2: User clicked on a job to view details
    // 3: User clicked back button - reset to filtered view
    if (loadCountRef.current >= 3) {
      loadCountRef.current = 0
      // Reset iframe to show filtered RSN Ginfo Solutions jobs
      setIframeKey(prevKey => prevKey + 1)
    }
  }


  const benefits = [
    {
      icon: '💰',
      title: 'Competitive Salary',
      description: 'Industry-leading compensation packages with performance bonuses'
    },
    {
      icon: '🏥',
      title: 'Health Benefits',
      description: 'Comprehensive health insurance for you and your family'
    },
    {
      icon: '🌴',
      title: 'Work-Life Balance',
      description: 'Flexible working hours and generous vacation policy'
    },
    {
      icon: '📚',
      title: 'Learning & Development',
      description: 'Continuous learning opportunities and certification support'
    },
    {
      icon: '🚀',
      title: 'Career Growth',
      description: 'Clear career progression paths and leadership opportunities'
    },
    {
      icon: '🏢',
      title: 'Modern Workspace',
      description: 'State-of-the-art facilities and collaborative environment'
    }
  ]

  return (
    <Container>
      {/* Hero Section */}
      <HeroSection>
        <MaxWidthContainer>
          <HeroContent>
            <FadeInUp>
              <HeroLabel>Join Our Team</HeroLabel>
            </FadeInUp>
            <FadeInUp delay={0.1}>
              <HeroTitle>Build Your Career With Us</HeroTitle>
            </FadeInUp>
            <FadeInUp delay={0.2}>
              <HeroSubtitle>
                Join a diverse team of 150+ talented professionals in a positive, collaborative environment where innovation flourishes and creativity thrives daily.
              </HeroSubtitle>
            </FadeInUp>
          </HeroContent>
        </MaxWidthContainer>
      </HeroSection>

      {/* Benefits Section */}
      <BenefitsSection>
        <MaxWidthContainer>
          <SectionHeader>
            <FadeInUp>
              <SectionLabel>Perks & Benefits</SectionLabel>
            </FadeInUp>
            <FadeInUp delay={0.1}>
              <SectionTitle>Why Work With Us</SectionTitle>
            </FadeInUp>
            <FadeInUp delay={0.2}>
              <SectionSubtitle>
                We offer comprehensive benefits to support your well-being, growth, and success at every stage of your career.
              </SectionSubtitle>
            </FadeInUp>
          </SectionHeader>

          <StaggerContainer staggerDelay={0.1}>
            <BenefitsGrid>
              {benefits.map((benefit, index) => (
                <StaggerItem key={index}>
                  <HoverCard>
                    <BenefitCard>
                      <BenefitIcon>{benefit.icon}</BenefitIcon>
                      <BenefitTitle>{benefit.title}</BenefitTitle>
                      <BenefitDescription>{benefit.description}</BenefitDescription>
                    </BenefitCard>
                  </HoverCard>
                </StaggerItem>
              ))}
            </BenefitsGrid>
          </StaggerContainer>
        </MaxWidthContainer>
      </BenefitsSection>

      {/* Open Positions Section */}
      <JobsSection>
        <MaxWidthContainer>
          <SectionHeader>
            <FadeInUp>
              <SectionLabel>Current Openings</SectionLabel>
            </FadeInUp>
            <FadeInUp delay={0.1}>
              <SectionSubtitle>
                Find your next opportunity and join our growing team of innovators and problem solvers.
              </SectionSubtitle>
            </FadeInUp>
          </SectionHeader>

          <FadeInUp delay={0.3}>
            <IframeContainer>
              <iframe
                key={iframeKey}
                src={ZENOHIRE_URL}
                style={{ width: '100%', height: '2000px', border: 'none' }}
                scrolling="yes"
                title="RSNG Info Solutions Careers"
                onLoad={handleIframeLoad}
              />
            </IframeContainer>
          </FadeInUp>
        </MaxWidthContainer>
      </JobsSection>
    </Container>
  )
}

export default RSNGCareers
